import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Cigarette, Dog, Sparkles, Clock, FileText, Info, Calendar, Shield,
  CheckCircle, XCircle, Package, Zap, Timer, AlertTriangle, FileCheck,
} from 'lucide-react';
import CollapsibleText from './CollapsibleText';

/**
 * SpecialtyRulesCard
 * ----------------------------------------------------------------------------
 * Category-aware rules panel rendered INSTEAD of <VehicleRulesCard> when
 * `vehicle.vehicle_category === 'specialty_asset'`. Strictly additive.
 *
 * Rule resolution:
 *   - Per-listing overrides live on `vehicle.specialty_rules` (optional dict).
 *     Each recognised key is read ONLY from that dict.
 *   - If the host has NOT configured specialty_rules (undefined/null), we
 *     fall back to a safe default set so existing listings keep rendering
 *     sensible rules without requiring a migration.
 *   - custom_rules_text is a host-editable free-text block and is always
 *     honoured when present.
 *
 * Vehicle-only concerns (kms/fuel/driver age) are NEVER displayed here.
 */

// Safe defaults used ONLY when a listing has no `specialty_rules` dict yet.
const LEGACY_DEFAULTS = Object.freeze({
  event_only: true,
  no_public_trading: true,
  no_modifications: true,
  must_return_clean: true,
  damage_reporting_required: true,
  follow_power_requirements: true,
  follow_setup_packdown: true,
  renter_responsible_permits: false,
  renter_requires_insurance: false,
});

const resolveSpecialtyRules = (vehicle) => {
  const supplied = vehicle?.specialty_rules;
  if (supplied && typeof supplied === 'object') {
    // When the host has configured any rules, read strictly from the dict.
    // Undefined keys fall back to the legacy-safe defaults (non-breaking).
    return { ...LEGACY_DEFAULTS, ...supplied };
  }
  return { ...LEGACY_DEFAULTS };
};

const getCancellationText = (policy) => {
  switch (policy) {
    case 'flexible':
      return { label: 'Flexible', desc: 'Full refund up to 24 hours before pickup', color: 'bg-green-100 text-green-700' };
    case 'moderate':
      return { label: 'Moderate', desc: 'Full refund up to 5 days before pickup', color: 'bg-blue-100 text-blue-700' };
    case 'strict':
      return { label: 'Strict', desc: '50% refund up to 7 days before pickup', color: 'bg-amber-100 text-amber-700' };
    default:
      return { label: 'Moderate', desc: 'Full refund up to 5 days before pickup', color: 'bg-blue-100 text-blue-700' };
  }
};

const RuleLine = ({ icon: Icon, iconBg = 'bg-slate-100', iconColor = 'text-slate-600', title, desc, testId }) => (
  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl" data-testid={testId}>
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
      <Icon className={`w-4 h-4 ${iconColor}`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-slate-900">{title}</p>
      {desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}
    </div>
  </div>
);

const SpecialtyRulesCard = ({
  rules = {},
  vehicle = {},
  compact = false,
  showTitle = true,
  className = '',
}) => {
  const specialty = resolveSpecialtyRules(vehicle);
  const cs = vehicle.category_specs || {};

  // Shared host-level flags honoured for both vehicles and specialty assets.
  const smokingAllowed = rules.smoking_allowed === true;
  const petsAllowed = rules.pets_allowed === true;
  const detailingFee = rules.detailing_fee || 0;
  const customHostText = specialty.custom_rules_text || rules.additional_rules || rules.custom_rules || '';
  const lateReturnFee = rules.late_return_fee_per_hour || 0;
  const cancellationPolicy = vehicle.cancellation_policy || 'moderate';
  const cancellation = getCancellationText(cancellationPolicy);

  // Generator clarity — uses category_specs which is populated via VehicleForm.
  const generatorSupported = cs.generator_supported === true;
  const generatorIncluded = cs.generator_included === true;

  if (compact) {
    return (
      <div className={`space-y-2 ${className}`} data-testid="specialty-rules-compact">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4 text-[#b45309]" />
          <span className="text-sm font-medium text-slate-700">Asset Rules</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {specialty.event_only && (
            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
              <Shield className="w-3 h-3 mr-1" /> Event / private use only
            </Badge>
          )}
          {specialty.no_public_trading && (
            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
              <XCircle className="w-3 h-3 mr-1" /> No unauthorised public trading
            </Badge>
          )}
          {specialty.must_return_clean && (
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
              <Sparkles className="w-3 h-3 mr-1" /> Return clean &amp; repacked
            </Badge>
          )}
        </div>
      </div>
    );
  }

  return (
    <Card className={`border-slate-100 ${className}`} data-testid="specialty-rules-card">
      {showTitle && (
        <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
          <CardTitle className="font-heading flex items-center gap-2 text-base sm:text-lg">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-[#b45309]" />
            Specialty Asset Rules
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className={`space-y-3 ${showTitle ? 'p-4 pt-0 sm:p-6 sm:pt-0' : 'p-4 sm:p-6'}`}>
        {specialty.event_only && (
          <RuleLine icon={Shield} iconBg="bg-amber-100" iconColor="text-amber-600"
            title="For event / private function use only"
            desc="This asset is hired for the agreed private event or function and not for unrelated commercial use."
            testId="specialty-rule-event-only" />
        )}

        {specialty.no_public_trading && (
          <RuleLine icon={XCircle} iconBg="bg-red-100" iconColor="text-red-600"
            title="No unauthorised public trading"
            desc="No trading at public markets, festivals or events without the host's written consent."
            testId="specialty-rule-no-trading" />
        )}

        {specialty.no_modifications && (
          <RuleLine icon={AlertTriangle} iconBg="bg-amber-100" iconColor="text-amber-600"
            title="No modification of the asset or equipment"
            desc="Do not rewire, re-plumb, repaint or alter fixed equipment. Any required change must be approved by the host first."
            testId="specialty-rule-no-mods" />
        )}

        {specialty.must_return_clean && (
          <RuleLine icon={Sparkles} iconBg="bg-blue-100" iconColor="text-blue-600"
            title="Return cleaned and repacked"
            desc={detailingFee > 0 ? `Extra cleaning fee may apply: $${Number(detailingFee).toFixed(2)}` : 'Return the asset in the condition it was handed over — cleaned and repacked as agreed.'}
            testId="specialty-rule-clean-repack" />
        )}

        {specialty.damage_reporting_required && (
          <RuleLine icon={AlertTriangle} iconBg="bg-red-100" iconColor="text-red-600"
            title="Report any damage immediately"
            desc="Damage, faults, leaks or safety issues must be reported to the host as soon as they occur."
            testId="specialty-rule-damage-report" />
        )}

        {specialty.follow_power_requirements && (
          <RuleLine icon={Zap} iconBg="bg-yellow-100" iconColor="text-yellow-600"
            title="Follow the asset's power setup"
            desc={cs.power_source_type
              ? `Power source: ${cs.power_source_type}. Use only the supplied leads and approved circuits — do not exceed the asset's electrical rating.`
              : 'Use only the supplied leads and approved circuits — do not exceed the asset\'s electrical rating.'}
            testId="specialty-rule-power" />
        )}

        {/* Generator clarity — sourced from category_specs, displayed only for specialty assets */}
        {generatorIncluded && (
          <RuleLine icon={CheckCircle} iconBg="bg-emerald-100" iconColor="text-emerald-600"
            title="Generator included with setup"
            desc="A suitable generator is provided with this hire. Use as instructed by the host."
            testId="specialty-rule-generator-included" />
        )}
        {!generatorIncluded && generatorSupported && (
          <RuleLine icon={AlertTriangle} iconBg="bg-amber-100" iconColor="text-amber-600"
            title="Generator not included — renter must supply"
            desc="This asset supports generator use but a generator is not provided. The renter must supply a suitable unit."
            testId="specialty-rule-generator-not-included" />
        )}

        {specialty.follow_setup_packdown && (
          <RuleLine icon={Timer} iconBg="bg-indigo-100" iconColor="text-indigo-600"
            title="Follow setup &amp; packdown instructions"
            desc={(cs.setup_time_minutes || cs.packdown_time_minutes)
              ? `Allow approximately ${cs.setup_time_minutes || '—'} min setup / ${cs.packdown_time_minutes || '—'} min packdown. Ensure the booking duration includes setup and pack-down time.`
              : 'Allow adequate time for setup and packdown. Ensure the booking duration includes setup and pack-down time.'}
            testId="specialty-rule-setup" />
        )}

        {specialty.renter_responsible_permits && (
          <RuleLine icon={FileCheck} iconBg="bg-purple-100" iconColor="text-purple-600"
            title="Renter is responsible for permits, approvals and venue permissions"
            desc="This may be required by councils or venues — check before your event."
            testId="specialty-rule-permits" />
        )}

        {specialty.renter_requires_insurance && (
          <RuleLine icon={Shield} iconBg="bg-slate-200" iconColor="text-slate-700"
            title="Renter must hold valid public liability insurance"
            desc="This may be required by venues or councils."
            testId="specialty-rule-insurance" />
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className={`p-3 rounded-xl flex items-center gap-3 ${smokingAllowed ? 'bg-green-50' : 'bg-red-50'}`} data-testid="specialty-rule-smoking">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${smokingAllowed ? 'bg-green-100' : 'bg-red-100'}`}>
              <Cigarette className={`w-4 h-4 ${smokingAllowed ? 'text-green-600' : 'text-red-600'}`} />
            </div>
            <p className={`text-sm font-medium ${smokingAllowed ? 'text-green-800' : 'text-red-800'}`}>
              {smokingAllowed ? 'Smoking permitted' : 'No smoking'}
            </p>
          </div>
          <div className={`p-3 rounded-xl flex items-center gap-3 ${petsAllowed ? 'bg-green-50' : 'bg-red-50'}`} data-testid="specialty-rule-pets">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${petsAllowed ? 'bg-green-100' : 'bg-red-100'}`}>
              <Dog className={`w-4 h-4 ${petsAllowed ? 'text-green-600' : 'text-red-600'}`} />
            </div>
            <p className={`text-sm font-medium ${petsAllowed ? 'text-green-800' : 'text-red-800'}`}>
              {petsAllowed ? 'Pets allowed' : 'Pets not allowed'}
            </p>
          </div>
        </div>

        {lateReturnFee > 0 && (
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl" data-testid="specialty-rule-late-fee">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <Clock className="w-4 h-4 text-orange-600" />
            </div>
            <p className="text-sm font-medium text-slate-900">Late return fee: ${lateReturnFee}/hour</p>
          </div>
        )}

        <div className={`rounded-xl p-4 flex items-start gap-3 ${cancellation.color.replace('text-', 'bg-').replace('700', '50').replace('800', '50')}`} data-testid="specialty-cancellation-policy">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cancellation.color.replace('text-', 'bg-').replace('700', '100').replace('800', '100')}`}>
            <Calendar className={`w-4 h-4 ${cancellation.color.replace('100', '600')}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-medium text-slate-900">Cancellation Policy</p>
              <Badge className={`text-xs ${cancellation.color}`}>{cancellation.label}</Badge>
            </div>
            <p className="text-xs text-slate-600">{cancellation.desc}</p>
          </div>
        </div>

        {customHostText && (
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-4" data-testid="specialty-rule-custom-text">
            <div className="flex items-start gap-3">
              <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 mb-1">Host's specialty notes:</p>
                <CollapsibleText
                  text={customHostText}
                  previewLength={180}
                  showMoreLabel="Show full notes"
                  showLessLabel="Hide notes"
                  textClassName="text-sm text-slate-600"
                />
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * SpecialtyRulesSummary — compact variant for the booking modal.
 * Mirrors TripRulesSummary's role but reads from vehicle.specialty_rules.
 */
export const SpecialtyRulesSummary = ({ rules = {}, vehicle = {}, className = '' }) => {
  const specialty = resolveSpecialtyRules(vehicle);
  const cs = vehicle.category_specs || {};
  const smokingAllowed = rules.smoking_allowed === true;
  const petsAllowed = rules.pets_allowed === true;
  const cancellationPolicy = vehicle.cancellation_policy || 'moderate';
  const cancellation = getCancellationText(cancellationPolicy);
  const generatorSupported = cs.generator_supported === true;
  const generatorIncluded = cs.generator_included === true;

  const items = [];
  if (specialty.event_only) items.push({ icon: Shield, text: 'Event / private function use only', color: 'text-amber-600' });
  if (specialty.no_public_trading) items.push({ icon: XCircle, text: 'No unauthorised public trading', color: 'text-red-600' });
  if (specialty.no_modifications) items.push({ icon: AlertTriangle, text: 'No modification of the asset', color: 'text-amber-600' });
  if (specialty.must_return_clean) items.push({ icon: Sparkles, text: 'Return cleaned & repacked', color: 'text-blue-600' });
  if (specialty.damage_reporting_required) items.push({ icon: AlertTriangle, text: 'Report damage immediately', color: 'text-red-600' });
  if (specialty.follow_power_requirements) items.push({ icon: Zap, text: 'Follow the asset\'s power setup', color: 'text-yellow-600' });
  if (generatorIncluded) items.push({ icon: CheckCircle, text: 'Generator included with setup', color: 'text-emerald-600' });
  if (!generatorIncluded && generatorSupported) items.push({ icon: AlertTriangle, text: 'Generator not included — renter must supply', color: 'text-amber-600' });
  if (specialty.follow_setup_packdown) items.push({ icon: Timer, text: 'Allow time for setup & packdown', color: 'text-indigo-600' });
  if (specialty.renter_responsible_permits) items.push({ icon: FileCheck, text: 'Renter responsible for permits', color: 'text-purple-600' });
  if (specialty.renter_requires_insurance) items.push({ icon: Shield, text: 'Renter must hold public liability insurance', color: 'text-slate-700' });
  items.push({ icon: smokingAllowed ? CheckCircle : XCircle, text: smokingAllowed ? 'Smoking permitted' : 'No smoking', color: smokingAllowed ? 'text-green-600' : 'text-red-600' });
  items.push({ icon: petsAllowed ? CheckCircle : XCircle, text: petsAllowed ? 'Pets allowed' : 'Pets not allowed', color: petsAllowed ? 'text-green-600' : 'text-red-600' });
  items.push({ icon: Calendar, text: `Cancellation: ${cancellation.label}`, color: 'text-slate-600' });

  return (
    <div className={`bg-slate-50 rounded-xl p-3 sm:p-4 ${className}`} data-testid="specialty-rules-summary">
      <div className="flex items-center gap-2 mb-3">
        <Package className="w-4 h-4 text-[#b45309]" />
        <span className="text-sm font-medium text-slate-700">Asset Rules</span>
      </div>
      <div className="space-y-2">
        {items.map((item, idx) => {
          const Icon = item.icon;
          return (
            <div key={idx} className="flex items-center gap-2 text-xs sm:text-sm">
              <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${item.color}`} />
              <span className="text-slate-700">{item.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SpecialtyRulesCard;
